import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiClock, FiMail, FiSend } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      details: '+447460008191',
      action: 'tel:+447460008191'
    },
    {
      icon: FiMapPin,
      title: 'Address',
      details: 'Coastwood Road, Gateshead',
      action: null
    },
    {
      icon: FiClock,
      title: 'Opening Hours',
      details: 'Open Daily - Call for specific hours',
      action: null
    },
    {
      icon: FiMail,
      title: 'Email',
      details: 'info@gatesheadhalalfood.com',
      action: 'mailto:info@gatesheadhalalfood.com'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-secondary-600 via-primary-600 to-accent-600 bg-clip-text text-transparent mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get in touch with us for any questions about our products or services.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div variants={itemVariants}>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent mb-8">Get In Touch</h2>
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow border border-primary-200"
                  whileHover={{ x: 5 }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-secondary-100 via-primary-100 to-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={info.icon} className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {info.title}
                    </h3>
                    {info.action ? (
                      <a
                        href={info.action}
                        className="text-gray-700 hover:text-primary-600 transition-colors"
                      >
                        {info.details}
                      </a>
                    ) : (
                      <p className="text-gray-700">{info.details}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Store Images */}
            <motion.div 
              variants={itemVariants}
              className="mt-8 grid grid-cols-2 gap-4"
            >
              <img
                src="https://i.ibb.co/wrKc7vhd/Gateshead-Halal-Food-Store-image-17.jpg"
                alt="Store Front"
                className="rounded-lg shadow-lg w-full h-32 object-cover border-2 border-primary-200"
              />
              <img
                src="https://i.ibb.co/PsDpdSn1/Gateshead-Halal-Food-Store-image-19.jpg"
                alt="Store Interior"
                className="rounded-lg shadow-lg w-full h-32 object-cover border-2 border-primary-200"
              />
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={itemVariants}>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent mb-8">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 border border-primary-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="+44 7xxx xxx xxx"
                />
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Tell us how we can help you..."
                />
              </div>
              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-secondary-600 to-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-secondary-700 hover:to-primary-700 transition-colors flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiSend} className="h-5 w-5" />
                <span>Send Message</span>
              </motion.button>
            </form>
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div 
          variants={itemVariants}
          className="text-center mt-16 bg-gradient-to-r from-secondary-600 via-primary-600 to-accent-600 rounded-lg p-8 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">Visit Our Store Today</h2>
          <p className="text-xl mb-6">
            Come and experience our fresh halal products and friendly service in person.
          </p>
          <a
            href="tel:+447460008191"
            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center space-x-2 shadow-lg"
          >
            <SafeIcon icon={FiPhone} className="h-5 w-5" />
            <span>Call Now: +447460008191</span>
          </a>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Contact;